import { useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard, faQrcode, faCircleCheck, faCircleXmark, faCopy, faClock, faRocket, faSpinner,
  faCamera, faUtensils, faRulerVertical, faLightbulb, faChartLine, faEnvelopeOpenText, faStar,
  faWandMagicSparkles, faDumbbell, faArrowTrendUp, faMedal, faChevronRight,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./PaymentsModal.module.css";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useToast } from "./ToastProvider";

const STATUS_LABELS = {
  pending: "Aguardando pagamento",
  confirmed: "Confirmado",
  received: "Pago",
  overdue: "Vencido",
  refunded: "Estornado",
  failed: "Falhou",
  active: "Ativa",
  canceled: "Cancelada",
  expired: "Expirada",
};

const METHOD_LABELS = { pix: "Pix", credit_card: "Cartão de crédito" };

// Mesmo mapa usado em ProfessorBillingModal.jsx / backend (admin.py MRR,
// subscription_service.py INTERVAL_TO_ASAAS_CYCLE — R160, docs/07-issues.md).
const MONTHS_PER_PLAN_INTERVAL = {
  weekly: 12 / 52, biweekly: 12 / 26, monthly: 1, bimonthly: 2,
  quarterly: 3, semiannual: 6, yearly: 12,
};

// Mantido em sincronia manualmente com o que é gateado em
// app/services/payments/access.py e com a tabela de comparação Free x Plus
// da landing page (landing/index.html, seção "Planos") - sem endpoint que
// liste isso dinamicamente.
// Lista COMPLETA (usada na tela "chegando em breve" e na de pagamento
// confirmado) - a vitrine de venda (PLUS_SELLING_POINTS, abaixo) é uma
// versão CURADA/reenquadrada como pitch pra decisão, não um resumo mecânico
// desta. Mas os FATOS (cadência de cada item) têm que bater nas duas -
// achado Crítico C1 da auditoria (docs/audit-payments-modal-plus.md): antes
// desta lista não mencionava cadência nenhuma e "renovação ilimitada" só
// aparecia na vitrine nova, criando 2 histórias diferentes pro mesmo pitch
// dependendo de onde o aluno entrava no fluxo.
const PLUS_BENEFITS = [
  {
    icon: faChartLine,
    title: "Treinos que evoluem com você",
    description: "Progressão de carga e volume ajustada automaticamente a cada renovação, com base no seu histórico real.",
  },
  {
    icon: faArrowTrendUp,
    title: "Renovações de treino sem limite",
    description: "No Free o treino renova só uma vez. No Plus, renove quantas vezes quiser, sempre a cada 90 dias (o intervalo pro corpo se adaptar é o mesmo dos dois planos).",
  },
  {
    icon: faUtensils,
    title: "Plano de nutrição",
    description: "Dieta personalizada por IA, revisada mensalmente - janela maior de dados (peso, refeições, aderência) pra um ajuste mais preciso.",
  },
  {
    icon: faRulerVertical,
    title: "Medidas corporais",
    description: "Registre e acompanhe percentual de gordura, TMB e outras medidas a cada 7 dias.",
  },
  {
    icon: faCamera,
    title: "Fotos de progresso",
    description: "Registre fotos a cada 7 dias e veja o comparativo antes/depois gerado automaticamente por IA.",
  },
  {
    icon: faLightbulb,
    title: "Insights de evolução com IA",
    description: "Receba análises semanais sobre aderência, volume e progresso do seu treino.",
  },
  {
    icon: faEnvelopeOpenText,
    title: "Relatórios por email",
    description: "Receba um resumo periódico da sua evolução direto na caixa de entrada.",
  },
];

// Mantido em sincronia manualmente com a seção "Free (já entregue)" de
// docs/01-produto.md (Modelo de Negócio) - mostrado no aviso de "Plus
// chegando em breve" pra deixar claro que essas features não dependem dele.
const FREE_BENEFITS = [
  {
    icon: faWandMagicSparkles,
    title: "Treino com IA",
    description: "Ficha renovada a cada 90 dias.",
  },
  {
    icon: faDumbbell,
    title: "Registro de treino",
    description: "Veja o que fez da última vez.",
  },
  {
    icon: faArrowTrendUp,
    title: "Evolução",
    description: "Streak, meta semanal e volume.",
  },
  {
    icon: faMedal,
    title: "Ranking",
    description: "Compare-se com outros alunos.",
  },
];

// ── Flag da vitrine de conversão do Plus ─────────────────────────────────────
// true  = nova vitrine (hero + Free×Plus + benefícios como resultado) no lugar
//         da faixa de pills fraca. false = comportamento antigo (faixa de chips).
// Só a seção "por quê" muda; os cards de plano e o checkout são os mesmos.
const SALES_PAYWALL = true;

// Benefícios reescritos como RESULTADO (não recurso) — é o que converte.
// IMPORTANTE: o treino renova a cada 90 dias pra Free E Plus (é o intervalo
// pro corpo se adaptar — não é limitação de plano). O Plus não acelera isso.
// O que o Plus entrega: acompanhamento a cada 7 dias (fotos, insights) e
// renovações de treino ILIMITADAS (o Free trava depois de 1 renovação).
// Nunca prometer "treino evolui mais rápido".
// A dieta é o ÚNICO item com janela própria: revisão mensal (não semanal)
// É PROPOSITAL — janela maior = mais dado real (aderência, peso, refeições
// registradas) pra IA ajustar com mais precisão do que trocar toda semana.
// Por isso a descrição enquadra o mês como vantagem, não como "menos
// frequente" — não usar o padrão "a cada 7 dias" pra esse item (confirmado
// que a cadência de fato é mensal, ver conversa sobre o audit da vitrine).
const PLUS_SELLING_POINTS = [
  { icon: faCamera,      title: "Acompanhamento toda semana", description: "Registro e análise de fotos, medidas corporais e insights de evolução a cada 7 dias" },
  { icon: faUtensils,    title: "Dieta ajustada com base no seu mês inteiro", description: "Plano alimentar por IA revisado mensalmente - janela maior de dados (peso, refeições, aderência) pra um ajuste mais preciso do que mudar toda semana." },
  { icon: faArrowTrendUp, title: "Sem travar depois da 1ª renovação", description: "Renove seu treino quantas vezes quiser, sempre a cada 90 dias." },
  { icon: faChartLine,   title: "Toda renovação usa seu histórico", description: "Medidas, peso, fotos, insights e registro de treinos entram no cálculo do seu próximo ciclo." },
];

// Piso mínimo pra mostrar a contagem real de alunos na vitrine (achado
// "Alta" da auditoria - prova social real em vez de depoimento inventado,
// ver GET /payments/stats). Número pequeno demais passa a impressão
// contrária à pretendida; ajuste esse piso quando a base crescer.
const MIN_STUDENTS_TO_SHOW_PROOF = 10;

const FINAL_PAYMENT_STATUSES = ["confirmed", "received", "overdue", "refunded", "failed"];
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 45; // ~3 minutos

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Erro inesperado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || (typeof d === "string" ? d : JSON.stringify(d))).join(" - ");
  }
  return JSON.stringify(detail);
}

// Mesmo algoritmo de dígito verificador do backend (_only_digits em
// app/schemas/pydantic.py) - front só antecipa a checagem pra não gastar um
// round-trip com um CPF/CNPJ que a API já sabe que vai rejeitar.
function checkDigit(digits, weights) {
  const total = digits.split("").reduce((sum, d, i) => sum + Number(d) * weights[i], 0);
  const rem = total % 11;
  return rem < 2 ? 0 : 11 - rem;
}

function isValidCpfCnpj(digits) {
  if (![11, 14].includes(digits.length)) return false;
  if (new Set(digits).size === 1) return false; // "00000000000" etc.
  if (digits.length === 11) {
    const d1 = checkDigit(digits.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
    const d2 = checkDigit(digits.slice(0, 9) + d1, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
    return digits.slice(-2) === `${d1}${d2}`;
  }
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = checkDigit(digits.slice(0, 12), w1);
  const d2 = checkDigit(digits.slice(0, 12) + d1, w2);
  return digits.slice(-2) === `${d1}${d2}`;
}

function formatMoney(value) {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch (_) {
    return iso;
  }
}

function LastPaymentInfo({ method, date }) {
  if (!method || !date) return null;
  return (
    <p className={styles.lastPaymentInfo}>
      Último pagamento: {formatDate(date)} via {METHOD_LABELS[method] || method}
    </p>
  );
}

function PixQrBlock({ qrImage, qrPayload }) {
  const toast = useToast();

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(qrPayload)
      .then(() => toast.success("Chave Pix copiada"))
      .catch(() => {});
  };

  return (
    <>
      {qrImage && (
        <img
          src={`data:image/png;base64,${qrImage}`}
          alt="QR Code Pix"
          className={styles.qrImage}
        />
      )}
      {qrPayload && (
        <div className={styles.pixCopyRow}>
          <code className={styles.pixCode}>{qrPayload}</code>
          <button type="button" className={styles.copyBtn} onClick={handleCopy}>
            <FontAwesomeIcon icon={faCopy} /> Copiar
          </button>
        </div>
      )}
    </>
  );
}

export default function PaymentsModal({ open, onClose, currentUser, onPaymentConfirmed }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [oneOffAccess, setOneOffAccess] = useState(null); // último avulso pago (válido ou expirado)

  const [billingKind, setBillingKind] = useState("subscription"); // subscription | oneoff
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [method, setMethod] = useState("pix"); // pix | credit_card
  const [cpfCnpj, setCpfCnpj] = useState("");

  const [card, setCard] = useState({ holder_name: "", number: "", expiry_month: "", expiry_year: "", ccv: "" });
  const [holder, setHolder] = useState({ postal_code: "", address_number: "", address_complement: "", mobile_phone: "" });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null); // { kind: 'payment'|'subscription', data, pix }
  const [liveStatus, setLiveStatus] = useState(null);

  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // Contagem real de alunos ativos, pro slot de prova social da vitrine.
  // Best-effort e fora do Promise.all de load() de propósito - se esse
  // endpoint falhar, só o número some (o resto do modal continua normal).
  const [activeStudents, setActiveStudents] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/payments/stats")
      .then(r => setActiveStudents(r.data?.active_students ?? null))
      .catch(() => {});
  }, [open]);

  // true só quando uma transição pendente→pago é detectada AO VIVO durante
  // esta abertura do modal (polling ou simulação dev) - nunca ao simplesmente
  // reabrir e encontrar uma assinatura/avulso já ativo de antes.
  const [justConfirmed, setJustConfirmed] = useState(false);

  // Seta indicando que a faixa de vantagens do Plus (.plusFeatureStrip) tem
  // mais itens pra rolar - some quando o scroll chega no fim, ou de cara se
  // os chips já cabem inteiros (tela larga o suficiente).
  const [plusStripScrollable, setPlusStripScrollable] = useState(true);
  const plusStripElRef = useRef(null);

  const checkPlusStripScroll = useCallback(() => {
    const el = plusStripElRef.current;
    if (!el) return;
    setPlusStripScrollable(el.scrollWidth - el.scrollLeft - el.clientWidth > 4);
  }, []);

  useEffect(() => {
    if (!open) return;
    checkPlusStripScroll();
    window.addEventListener("resize", checkPlusStripScroll);
    return () => window.removeEventListener("resize", checkPlusStripScroll);
  }, [open, checkPlusStripScroll]);

  // Ref do grid de planos - alvo do atalho "ver planos" logo abaixo do hero
  // da vitrine (SALES_PAYWALL), pra quem já decidiu pular a persuasão longa.
  const planGridRef = useRef(null);
  const scrollToPlans = useCallback(() => {
    planGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const pollRef = useRef(null);
  const cyclePollRef = useRef(null);
  const renewalPollRef = useRef(null);
  const [cyclePixData, setCyclePixData] = useState(null); // QR do 1º ciclo da assinatura Pix (chega async)
  const [cyclePolling, setCyclePolling] = useState(false);
  const [cycleChecking, setCycleChecking] = useState(false); // "Atualizar status" manual (fora do polling automático)

  useBodyScrollLock(open);

  const needsCpf = !currentUser?.cpf_cnpj;

  // Avulso = "1 mês", então só faz sentido cobrar o valor do plano mensal -
  // o anual é um desconto por compromisso de 12 meses, não pode ser pago
  // fracionado como avulso (o backend também bloqueia isso, ver checkout_service.py).
  const monthlyPlans = plans.filter(p => p.interval === "monthly");
  const visiblePlans = billingKind === "oneoff" ? monthlyPlans : plans;

  // Vitrine de preço (flag SALES_PAYWALL): baseline mensal p/ calcular economia
  // e qual plano é o "melhor valor" (menor mensalidade efetiva = âncora alta).
  const monthlyBaseline = monthlyPlans[0]?.amount || 0;
  const bestValuePlanId = (() => {
    let best = null, bestEff = Infinity;
    for (const p of plans) {
      if (!p.amount) continue;
      const m = MONTHS_PER_PLAN_INTERVAL[p.interval] || 1;
      const eff = p.amount / m;
      if (eff < bestEff) { bestEff = eff; best = p.id; }
    }
    return best;
  })();

  function handleBillingKindChange(kind) {
    setBillingKind(kind);
    const pool = kind === "oneoff" ? monthlyPlans : plans;
    if (!pool.some(p => p.id === selectedPlanId)) {
      setSelectedPlanId(pool[0]?.id ?? null);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [plansRes, subRes, pendingRes, accessRes] = await Promise.all([
        api.get("/payments/plans"),
        api.get("/payments/subscriptions/me"),
        api.get("/payments/pending"),
        api.get("/payments/access"),
      ]);
      setPlans(plansRes.data);
      setSubscription(subRes.data);
      setOneOffAccess(accessRes.data);
      if (plansRes.data.length > 0) {
        let defId = plansRes.data[0].id;
        if (SALES_PAYWALL) {
          let bestEff = Infinity;
          for (const p of plansRes.data) {
            if (!p.amount) continue;
            const m = MONTHS_PER_PLAN_INTERVAL[p.interval] || 1;
            const eff = p.amount / m;
            if (eff < bestEff) { bestEff = eff; defId = p.id; }
          }
        }
        setSelectedPlanId(defId);
      }

      // Avulso já em aberto (Pix ou cartão em análise de risco na Asaas) -
      // mostra ele em vez do formulário normal, pra não deixar criar um
      // segundo em paralelo (ver checkout_service.py).
      const pending = pendingRes.data;
      if (pending) {
        setResult({ kind: "payment", data: pending, pix: pending.pix });
        setLiveStatus(pending.status);
        if (!FINAL_PAYMENT_STATUSES.includes(pending.status)) {
          startPolling(pending.id);
        }
      }
    } catch (err) {
      setLoadError(extractError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setLiveStatus(null);
      setSubmitError("");
      setCancelConfirm(false);
      setCancelError("");
      setCyclePixData(null);
      setCyclePolling(false);
      setJustConfirmed(false);
      clearInterval(pollRef.current);
      clearInterval(cyclePollRef.current);
      clearInterval(renewalPollRef.current);
      return;
    }
    load();
  }, [open, load]);

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(cyclePollRef.current);
    clearInterval(renewalPollRef.current);
  }, []);

  // Cobrança de ciclo de renovação (assinante já ativo, Pix não tem débito
  // automático) - mesmo padrão de detecção ao vivo dos outros fluxos, mas sem
  // "justConfirmed": quem já é assinante ativo não precisa da tela de boas-
  // vindas com a lista de benefícios, só precisa que a caixinha de cobrança
  // pendente suma sozinha assim que o Pix for pago.
  useEffect(() => {
    const pendingId = subscription?.pending_cycle_payment?.id;
    clearInterval(renewalPollRef.current);
    if (!pendingId) return;

    let attempts = 0;
    renewalPollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/${pendingId}/status`);
        if (["received", "confirmed"].includes(data.status)) {
          clearInterval(renewalPollRef.current);
          onPaymentConfirmed?.();
          load();
          return;
        }
      } catch (_) {
        // segue tentando - falha pontual não deve interromper o polling
      }
      if (attempts >= POLL_MAX_ATTEMPTS) clearInterval(renewalPollRef.current);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(renewalPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.pending_cycle_payment?.id]);

  function startPolling(paymentId) {
    let attempts = 0;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/${paymentId}/status`);
        setLiveStatus(data.status);
        if (["received", "confirmed"].includes(data.status)) {
          clearInterval(pollRef.current);
          setJustConfirmed(true);
          onPaymentConfirmed?.();
          load();
          return;
        }
        if (FINAL_PAYMENT_STATUSES.includes(data.status) || attempts >= POLL_MAX_ATTEMPTS) {
          clearInterval(pollRef.current);
        }
      } catch (_) {
        if (attempts >= POLL_MAX_ATTEMPTS) clearInterval(pollRef.current);
      }
    }, POLL_INTERVAL_MS);
  }

  // Uma checagem do ciclo pendente - compartilhada pelo polling automático
  // (startCyclePolling) e pelo botão manual "Atualizar status" (pro caso do
  // aluno demorar mais que a janela de polling pra pagar, ver
  // POLL_MAX_ATTEMPTS - sem isso ele ficava preso na tela "Assinatura
  // criada"/QR até fechar e reabrir o modal). Retorna true se detectou
  // pagamento confirmado (quem chamou não precisa fazer mais nada depois).
  async function checkCycleOnce() {
    try {
      const { data } = await api.get("/payments/subscriptions/me");
      if (data?.pending_cycle_payment?.pix?.qr_code_payload) {
        setCyclePixData(data.pending_cycle_payment.pix);
      }
      // subscription.status já vem "active" da Asaas desde a CRIAÇÃO da
      // assinatura, antes até do Pix ser pago (Pix não é débito
      // automático) - não dá pra usar isso como sinal de confirmação.
      // last_payment só existe quando há um ciclo REALMENTE pago (ver
      // access.py, mesma checagem usada no backend pra liberar o Plus).
      if (data?.last_payment) {
        clearInterval(cyclePollRef.current);
        setCyclePolling(false);
        setJustConfirmed(true);
        onPaymentConfirmed?.();
        load();
        return true;
      }
    } catch (_) {
      // segue tentando - falha pontual não deve interromper o polling
    }
    return false;
  }

  // Assinatura via Pix: a Asaas gera a cobrança do 1º ciclo de forma
  // assíncrona (não vem na resposta de criar a assinatura) - sem isso, o
  // QR só apareceria se o usuário fechasse e reabrisse o modal.
  function startCyclePolling() {
    let attempts = 0;
    setCyclePolling(true);
    clearInterval(cyclePollRef.current);
    cyclePollRef.current = setInterval(async () => {
      attempts += 1;
      // Continuamos o mesmo polling (não paramos só por ter achado o QR)
      // até essa transição, pra não depender de reload manual.
      const confirmed = await checkCycleOnce();
      if (confirmed) return;
      if (attempts >= POLL_MAX_ATTEMPTS) {
        setCyclePolling(false);
        clearInterval(cyclePollRef.current);
      }
    }, POLL_INTERVAL_MS);
  }

  // Botão "Atualizar status": cobre quem demora mais que ~3min (janela do
  // polling automático) pra escanear o Pix. Sem isso a tela ficava presa em
  // "Assinatura criada"/aguardando, sem nenhum jeito de saber que o
  // pagamento já tinha caído a não ser fechando e reabrindo o modal inteiro.
  async function handleRefreshCycleStatus() {
    setCycleChecking(true);
    await checkCycleOnce();
    setCycleChecking(false);
  }

  const [simulating, setSimulating] = useState(false);
  const [simulateError, setSimulateError] = useState("");

  const [cancelingPayment, setCancelingPayment] = useState(false);
  const [cancelPaymentError, setCancelPaymentError] = useState("");
  const [confirmCancelPayment, setConfirmCancelPayment] = useState(false);

  async function handleCancelPendingPayment(paymentId) {
    setCancelingPayment(true);
    setCancelPaymentError("");
    try {
      await api.delete(`/payments/${paymentId}`);
      clearInterval(pollRef.current);
      setConfirmCancelPayment(false);
      setResult(null);
      setLiveStatus(null);
      await load();
    } catch (err) {
      setCancelPaymentError(extractError(err));
    } finally {
      setCancelingPayment(false);
    }
  }

  async function handleSimulateConfirm(paymentId) {
    setSimulating(true);
    setSimulateError("");
    try {
      const { data } = await api.post(`/payments/${paymentId}/simulate-confirm`);
      setLiveStatus(data.status);
      clearInterval(pollRef.current);
      if (["received", "confirmed"].includes(data.status)) {
        setJustConfirmed(true);
        onPaymentConfirmed?.();
        await load();
      }
    } catch (err) {
      setSimulateError(err.response?.data?.detail || err.message);
    } finally {
      setSimulating(false);
    }
  }

  // Mesmo endpoint da simulação acima, mas pra cobrança de ciclo de
  // assinatura: em vez de atualizar liveStatus (que é do fluxo de avulso),
  // recarrega tudo - a caixa de "cobrança pendente" some sozinha e o período
  // da assinatura já vem sincronizado (ver _sync_subscription no backend).
  async function handleSimulateCycleConfirm(paymentId) {
    setSimulating(true);
    setSimulateError("");
    try {
      await api.post(`/payments/${paymentId}/simulate-confirm`);
      onPaymentConfirmed?.();
      await load();
    } catch (err) {
      setSimulateError(err.response?.data?.detail || err.message);
    } finally {
      setSimulating(false);
    }
  }

  async function handleSubmit() {
    setSubmitError("");
    if (!selectedPlanId) { setSubmitError("Selecione um plano."); return; }
    if (needsCpf) {
      if (!cpfCnpj.trim()) { setSubmitError("Informe seu CPF/CNPJ."); return; }
      // Mesma regra do backend (_only_digits em app/schemas/pydantic.py) -
      // valida aqui pra não gastar um round-trip com algo que a API já sabe
      // que vai rejeitar.
      if (![11, 14].includes(cpfCnpj.trim().length)) {
        setSubmitError("CPF deve ter 11 dígitos ou CNPJ 14 dígitos.");
        return;
      }
      if (!isValidCpfCnpj(cpfCnpj.trim())) {
        setSubmitError("CPF/CNPJ inválido.");
        return;
      }
    }

    const payload = { plan_id: selectedPlanId };
    if (needsCpf) payload.cpf_cnpj = cpfCnpj.trim();

    if (method === "credit_card") {
      if (!card.holder_name || !card.number || !card.expiry_month || !card.expiry_year || !card.ccv) {
        setSubmitError("Preencha todos os dados do cartão.");
        return;
      }
      // Mesmos limites do CreditCardIn em app/schemas/pydantic.py - formato,
      // não validade real do cartão (isso só a Asaas sabe dizer).
      if (card.number.length < 13 || card.number.length > 19) {
        setSubmitError("Número do cartão inválido.");
        return;
      }
      if (!/^(0[1-9]|1[0-2])$/.test(card.expiry_month)) {
        setSubmitError("Mês de validade inválido (MM, de 01 a 12).");
        return;
      }
      if (!/^\d{4}$/.test(card.expiry_year)) {
        setSubmitError("Ano de validade inválido (AAAA, 4 dígitos).");
        return;
      }
      if (card.ccv.length < 3 || card.ccv.length > 4) {
        setSubmitError("CVV inválido.");
        return;
      }
      if (!holder.postal_code || !holder.address_number) {
        setSubmitError("Informe CEP e número do endereço de cobrança.");
        return;
      }
      if (holder.postal_code.length < 8 || holder.postal_code.length > 9) {
        setSubmitError("CEP inválido.");
        return;
      }
      if (!holder.mobile_phone) {
        setSubmitError("Informe o telefone/celular do titular do cartão.");
        return;
      }
      payload.credit_card = { ...card };
      payload.credit_card_holder_info = {
        name: card.holder_name,
        email: currentUser?.email,
        cpf_cnpj: needsCpf ? cpfCnpj.trim() : currentUser?.cpf_cnpj,
        postal_code: holder.postal_code,
        address_number: holder.address_number,
        address_complement: holder.address_complement || undefined,
        mobile_phone: holder.mobile_phone || undefined,
      };
    }

    setSubmitting(true);
    try {
      if (billingKind === "subscription") {
        const { data } = await api.post("/payments/subscriptions", {
          ...payload,
          billing_type: method === "pix" ? "PIX" : "CREDIT_CARD",
        });
        setResult({ kind: "subscription", data });
        setSubscription(data);
        if (method === "pix") {
          // QR pode já vir pronto na resposta, mas o polling continua sendo
          // necessário pra detectar quando o aluno efetivamente paga esse QR
          // - ver startCyclePolling() (checa last_payment, não status).
          if (data?.pending_cycle_payment?.pix?.qr_code_payload) {
            setCyclePixData(data.pending_cycle_payment.pix);
          }
          startCyclePolling();
        } else if (data.last_payment) {
          // Cartão é síncrono - a Asaas já autoriza na hora da criação, sem
          // precisar de polling (diferente do Pix, que depende do aluno
          // escanear o QR depois). last_payment só vem preenchido quando o
          // backend já sincronizou uma cobrança realmente paga (ver
          // subscriptions.py _subscription_dict) - status "active" sozinho
          // não prova isso, a Asaas seta assim já na criação da assinatura.
          setJustConfirmed(true);
          onPaymentConfirmed?.();
          load();
        }
      } else {
        const { data } = await api.post("/payments/checkout", { ...payload, method });
        setResult({ kind: "payment", data, pix: data.pix });
        setLiveStatus(data.status);
        if (method === "pix" && data.id && !FINAL_PAYMENT_STATUSES.includes(data.status)) {
          startPolling(data.id);
        } else if (["received", "confirmed"].includes(data.status)) {
          // Cartão avulso também é síncrono - mesma lógica acima.
          setJustConfirmed(true);
          onPaymentConfirmed?.();
          load();
        }
      }
    } catch (err) {
      setSubmitError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancelSubscription() {
    setCanceling(true);
    setCancelError("");
    try {
      const { data } = await api.delete(`/payments/subscriptions/${subscription.id}`);
      setSubscription(data);
      setCancelConfirm(false);
    } catch (err) {
      setCancelError(extractError(err));
    } finally {
      setCanceling(false);
    }
  }

  if (!open) return null;

  const hasActiveSubscription = subscription && subscription.status === "active";
  const subscriptionPeriodEnd = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  // Cancelada MAS sem nenhum ciclo pago (ex: assinatura Pix cancelada antes
  // do QR ser pago) nao deve cair aqui - current_period_end vem preenchido
  // desde a criacao (nextDueDate do 1o ciclo na Asaas), mesmo que o aluno
  // nunca tenha efetivamente pago nada. Sem o check de last_payment, a tela
  // mostrava "acesso valido ate X" pra um acesso que nunca existiu, E
  // escondia a grade de planos (ver linha do "else" mais abaixo) - caminho
  // sem saida pro aluno tentar assinar de novo. subscription.last_payment so
  // vem preenchido quando existe Payment status received/confirmed nesse
  // ciclo (ver app/api/payments/subscriptions.py::_last_payment_dict) -
  // mesmo sinal que o backend usa em has_paid_access/_subscription_has_paid_cycle.
  const hasCanceledButValidSubscription =
    subscription && subscription.status === "canceled" && subscriptionPeriodEnd && subscriptionPeriodEnd > new Date() && !!subscription.last_payment;
  const effectiveStatus = liveStatus || result?.data?.status;

  // Assinatura é prioritária: o aviso/bloqueio de avulso só faz sentido pra
  // quem nunca assinou. Uma vez que existe uma relação de assinatura (ativa,
  // cancelada mas ainda válida, ou até já encerrada), essa é a narrativa que
  // importa na tela - o histórico de avulso vira irrelevante.
  const hasEverSubscribed = !!subscription;
  const oneOffExpiresAt = oneOffAccess?.expires_at ? new Date(oneOffAccess.expires_at) : null;
  const hasActiveOneOffAccess = !hasEverSubscribed && !!(oneOffExpiresAt && oneOffExpiresAt > new Date());
  const oneOffAccessExpired = !hasEverSubscribed && !!(oneOffAccess && oneOffExpiresAt && oneOffExpiresAt <= new Date());

  // Aluno com professor já tem acesso total a tudo (fotos, nutrição, medidas,
  // insights) independente de pagamento - o que ele paga aqui é a mensalidade
  // do próprio professor (ver professor_billing.py), não um desbloqueio de
  // feature. A copy de "Plus"/benefícios é exclusiva do self-service.
  const isStudentOfProfessor = currentUser?.role === "aluno" && !!currentUser?.professor_id;
  // Dono de academia paga o plano exclusivo dela (preço definido pelo admin,
  // ver app/services/payments/org_billing.py): sem vitrine do Plus e só por
  // assinatura (o backend recusa avulso de plano de academia).
  const isOrgOwner = currentUser?.org_role === "owner" && !!currentUser?.organization_id;
  const noPlusPitch = isStudentOfProfessor || isOrgOwner;

  // CTA final confirma o que o aluno está prestes a comprar (achado da
  // auditoria: botão genérico "Assinar"/"Pagar" não repetia plano nem preço
  // depois de toda a vitrine + grid - ver docs/audit-payments-modal-plus.md,
  // Crítico C3). Cai pro rótulo genérico se o plano ainda não resolveu.
  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const ctaVerb = billingKind === "subscription" ? "Assinar" : "Pagar";
  const ctaLabel = selectedPlan
    ? `${ctaVerb} ${selectedPlan.name} - ${formatMoney(selectedPlan.amount)}`
    : ctaVerb;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      style={SALES_PAYWALL ? { background: "rgba(6,12,24,0.86)", backdropFilter: "blur(2px)" } : undefined}
    >
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} disabled={submitting || canceling} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}><FontAwesomeIcon icon={faCreditCard} /></span>
            <div>
              <h2 className={styles.title}>{isOrgOwner ? "Assinatura da academia" : "Pagamentos"}</h2>
              <p className={styles.sub}>
                {isOrgOwner ? "Plano da academia, com o valor combinado com a TreinoNexo" : "Gerencie sua assinatura ou faça um pagamento avulso"}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {justConfirmed ? (
            <div className={styles.confirmedView}>
              <div className={styles.confirmedIcon}>
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <h3 className={styles.confirmedTitle}>Pagamento confirmado!</h3>
              {noPlusPitch ? (
                <p className={styles.confirmedText}>
                  {isOrgOwner
                    ? "A assinatura da academia foi registrada. O acesso dos professores está liberado."
                    : "Sua mensalidade foi registrada com sucesso."}
                </p>
              ) : (
                <>
                  <p className={styles.confirmedText}>
                    Seu plano Plus já está ativo. Aproveite os benefícios:
                  </p>
                  <div className={styles.benefitsList}>
                    {PLUS_BENEFITS.map(b => (
                      <div key={b.title} className={styles.benefitItem}>
                        <span className={styles.benefitIcon}><FontAwesomeIcon icon={b.icon} /></span>
                        <div>
                          <strong>{b.title}</strong>
                          <p>{b.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <button
                type="button"
                className={styles.btnPrimary}
                style={{ width: "100%" }}
                onClick={() => { setJustConfirmed(false); onClose(); }}
              >
                Começar a usar
              </button>
            </div>
          ) : loading ? (
            <div className={styles.loadingMsg}>Carregando…</div>
          ) : loadError ? (
            <div className={styles.error}>{loadError}</div>

          ) : result ? (
            <div className={styles.resultView}>
              {result.pix ? (
                <>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={
                      effectiveStatus === "pending" ? faSpinner :
                      effectiveStatus === "failed" ? faCircleXmark :
                      (effectiveStatus === "confirmed" || effectiveStatus === "received") ? faCircleCheck :
                      faClock
                    } spin={effectiveStatus === "pending"} />
                    {STATUS_LABELS[effectiveStatus] || effectiveStatus}
                  </div>
                  <PixQrBlock qrImage={result.pix.qr_code_image} qrPayload={result.pix.qr_code_payload} />
                  <p className={styles.hint}>
                    Pague com o app do seu banco. O status atualiza sozinho aqui assim que a confirmação chegar.
                  </p>
                  {effectiveStatus === "pending" && (
                    confirmCancelPayment ? (
                      <div className={styles.cancelRow}>
                        <span>Cancelar esse pagamento e começar outro?</span>
                        <button type="button" className={styles.btnSecondary}
                          onClick={() => setConfirmCancelPayment(false)} disabled={cancelingPayment}>
                          Voltar
                        </button>
                        <button type="button" className={styles.btnDanger}
                          onClick={() => handleCancelPendingPayment(result.data.id)} disabled={cancelingPayment}>
                          {cancelingPayment ? "Cancelando…" : "Confirmar cancelamento"}
                        </button>
                      </div>
                    ) : (
                      <button type="button" className={styles.linkBtn} onClick={() => setConfirmCancelPayment(true)}>
                        Cancelar este pagamento
                      </button>
                    )
                  )}
                  {cancelPaymentError && <div className={styles.error}>{cancelPaymentError}</div>}
                  {process.env.NODE_ENV === "development" && !FINAL_PAYMENT_STATUSES.includes(effectiveStatus) && (
                    <>
                      <button
                        type="button"
                        className={styles.devSimulateBtn}
                        onClick={() => handleSimulateConfirm(result.data.id)}
                        disabled={simulating}
                      >
                        {simulating ? "Simulando…" : "[DEV] Simular pagamento confirmado"}
                      </button>
                      {simulateError && <div className={styles.error}>{simulateError}</div>}
                    </>
                  )}
                </>
              ) : result.kind === "subscription" && method === "pix" ? (
                <>
                  <div className={styles.pixStatus}>
                    {cyclePixData ? (
                      <><FontAwesomeIcon icon={faSpinner} spin={cyclePolling || cycleChecking} /> {STATUS_LABELS.pending}</>
                    ) : (
                      <><FontAwesomeIcon icon={faClock} /> Assinatura criada</>
                    )}
                  </div>
                  <h3 className={styles.subPlanName}>{result.data.plan_name}</h3>
                  {cyclePixData ? (
                    <>
                      <PixQrBlock qrImage={cyclePixData.qr_code_image} qrPayload={cyclePixData.qr_code_payload} />
                      <p className={styles.hint}>
                        Pague o Pix do primeiro ciclo pra ativar sua assinatura.
                      </p>
                    </>
                  ) : (
                    <p className={styles.hint}>
                      {cyclePolling ? (
                        <><FontAwesomeIcon icon={faSpinner} spin /> Gerando o QR code Pix do primeiro ciclo…</>
                      ) : (
                        "A cobrança Pix do primeiro ciclo é gerada em instantes. Feche esta tela e volte em alguns segundos, ou confira seu e-mail."
                      )}
                    </p>
                  )}
                  <button
                    type="button"
                    className={styles.refreshLink}
                    onClick={handleRefreshCycleStatus}
                    disabled={cycleChecking}
                  >
                    {cycleChecking ? "Atualizando…" : "Atualizar status"}
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={
                      effectiveStatus === "pending" ? faSpinner :
                      effectiveStatus === "failed" ? faCircleXmark :
                      (effectiveStatus === "confirmed" || effectiveStatus === "received") ? faCircleCheck :
                      faClock
                    } spin={effectiveStatus === "pending"} />
                    {STATUS_LABELS[effectiveStatus] || effectiveStatus}
                  </div>
                  {result.kind === "subscription" ? (
                    <>
                      <h3 className={styles.subPlanName}>{result.data.plan_name}</h3>
                      {result.data.current_period_end && (
                        <p className={styles.hint}>Próxima cobrança: {formatDate(result.data.current_period_end)}</p>
                      )}
                    </>
                  ) : result.data?.method === "pix" && effectiveStatus === "pending" ? (
                    <>
                      <p className={styles.hint}>
                        Não conseguimos carregar o QR code agora - o pagamento continua pendente,
                        pode ser só uma falha passageira.
                      </p>
                      <button type="button" className={styles.refreshLink} onClick={load} disabled={loading}>
                        {loading ? "Atualizando…" : "Tentar novamente"}
                      </button>
                    </>
                  ) : result.data?.method === "credit_card" && effectiveStatus === "pending" ? (
                    <p className={styles.hint}>
                      <FontAwesomeIcon icon={faSpinner} spin />{" "}
                      Seu cartão está em análise pela Asaas. Isso é raro e costuma ser rápido - o
                      status atualiza sozinho aqui assim que a confirmação chegar.
                    </p>
                  ) : (
                    <p className={styles.hint}>Pagamento processado.</p>
                  )}
                </>
              )}
              <button className={styles.btnPrimary} onClick={onClose} style={{ width: "100%" }}>
                Fechar
              </button>
            </div>

          ) : hasActiveSubscription ? (
            <div className={styles.subStatus}>
              <div className={styles.subBadge}>{STATUS_LABELS[subscription.status] || subscription.status}</div>
              <h3 className={styles.subPlanName}>{subscription.plan_name}</h3>
              <p className={styles.subAmount}>{formatMoney(subscription.plan_amount)}/mês</p>
              <div className={styles.subMeta}>
                <span>Próxima cobrança: {formatDate(subscription.current_period_end)}</span>
              </div>
              <LastPaymentInfo method={subscription.last_payment?.method} date={subscription.last_payment?.date} />

              {subscription.pending_cycle_payment && (
                // Antes exigia "new Date() > current_period_end", mas current_period_end
                // já vem do nextDueDate DO PRÓXIMO ciclo retornado pela Asaas na criação da
                // assinatura (ela cobra o 1º ciclo na hora e já aponta o 2º) — então a
                // cobrança do 1º ciclo (a que está de fato pendente) tem due date ANTES do
                // period_end, e a condição de data nunca era satisfeita: a caixa (QR Pix +
                // botão [DEV] simular) nunca aparecia pro primeiro pagamento. O backend já
                // só popula pending_cycle_payment quando existe mesmo uma cobrança Pix
                // pendente da assinatura (ver _subscription_dict), então basta confiar nele.
                <div className={styles.cyclePaymentBox}>
                  <div className={styles.pixStatus}>
                    <FontAwesomeIcon icon={faClock} /> Cobrança deste ciclo pendente - {formatMoney(subscription.pending_cycle_payment.amount)}
                  </div>
                  <PixQrBlock
                    qrImage={subscription.pending_cycle_payment.pix?.qr_code_image}
                    qrPayload={subscription.pending_cycle_payment.pix?.qr_code_payload}
                  />
                  <p className={styles.hint}>
                    Pix não tem débito automático - pague esse QR pra manter sua assinatura em dia.
                  </p>
                  <button type="button" className={styles.refreshLink} onClick={load} disabled={loading}>
                    {loading ? "Atualizando…" : "Atualizar status"}
                  </button>
                  {process.env.NODE_ENV === "development" && (
                    <>
                      <button
                        type="button"
                        className={styles.devSimulateBtn}
                        onClick={() => handleSimulateCycleConfirm(subscription.pending_cycle_payment.id)}
                        disabled={simulating}
                      >
                        {simulating ? "Simulando…" : "[DEV] Simular pagamento confirmado"}
                      </button>
                      {simulateError && <div className={styles.error}>{simulateError}</div>}
                    </>
                  )}
                </div>
              )}

              {!cancelConfirm ? (
                <button className={styles.btnDanger} onClick={() => setCancelConfirm(true)}>
                  Cancelar assinatura
                </button>
              ) : (
                <div className={styles.confirmBox}>
                  <p>Cancelar sua assinatura? Você perde o acesso ao fim do período atual.</p>
                  {cancelError && <div className={styles.error}>{cancelError}</div>}
                  <div className={styles.confirmActions}>
                    <button className={styles.btnSecondary} onClick={() => setCancelConfirm(false)} disabled={canceling}>
                      Voltar
                    </button>
                    <button className={styles.btnDanger} onClick={handleCancelSubscription} disabled={canceling}>
                      {canceling ? "Cancelando…" : "Confirmar cancelamento"}
                    </button>
                  </div>
                </div>
              )}
            </div>

          ) : hasCanceledButValidSubscription ? (
            <div className={styles.subStatus}>
              <div className={`${styles.subBadge} ${styles.subBadgeCanceled}`}>Cancelada</div>
              <h3 className={styles.subPlanName}>{subscription.plan_name}</h3>
              <p className={styles.subAmount}>{formatMoney(subscription.plan_amount)}/mês</p>
              <p className={styles.hint}>
                Sua assinatura foi cancelada e não vai renovar, mas seu acesso continua
                válido até {formatDate(subscription.current_period_end)}.
              </p>
              <LastPaymentInfo method={subscription.last_payment?.method} date={subscription.last_payment?.date} />
            </div>

          ) : hasActiveOneOffAccess ? (
            <div className={styles.subStatus}>
              <div className={styles.subBadge}>Acesso ativo</div>
              <h3 className={styles.subPlanName}>Pagamento avulso</h3>
              <p className={styles.subAmount}>{oneOffAccess?.amount ? formatMoney(oneOffAccess.amount) : "-"}</p>
              <p className={styles.hint}>
                Válido até {formatDate(oneOffAccess.expires_at)}
              </p>
              <p className={styles.hint}>
                Não é possível assinar ou pagar avulso de novo antes dessa data.
              </p>
              <LastPaymentInfo method={oneOffAccess?.method} date={oneOffAccess?.updated_at} />
            </div>

          ) : (
            <div className={styles.form}>
              {oneOffAccessExpired && (
                <div className={styles.warningBanner}>
                  Seu plano avulso expirou em {formatDate(oneOffAccess.expires_at)}. Escolha uma opção abaixo para continuar.
                </div>
              )}
              {plans.length === 0 ? (
                <div className={styles.comingSoon}>
                  <div className={styles.comingSoonIcon}>
                    <FontAwesomeIcon icon={faRocket} />
                  </div>
                  {isOrgOwner ? (
                    <>
                      <h3 className={styles.comingSoonTitle}>Plano da academia ainda não definido</h3>
                      <p className={styles.comingSoonText}>
                        O valor da assinatura da academia é combinado com a equipe TreinoNexo. Assim que
                        for definido, ele aparece aqui para você assinar com Pix ou cartão.
                      </p>
                    </>
                  ) : isStudentOfProfessor ? (
                    <>
                      <h3 className={styles.comingSoonTitle}>Seu professor ainda não configurou cobrança</h3>
                      <p className={styles.comingSoonText}>
                        Quando seu professor ativar a cobrança de mensalidade pelo app, o plano
                        aparecerá aqui pra você pagar com Pix ou cartão.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className={styles.comingSoonTitle}>Plano Plus chegando em breve</h3>
                      <p className={styles.comingSoonText}>
                        Estamos com os últimos ajustes pra liberar assinatura e pagamento avulso.
                        Assim que estiver disponível, você poderá assinar direto por aqui.
                      </p>
                      <div className={styles.comingSoonDots}>
                        <span className={styles.comingSoonDot} />
                        <span className={styles.comingSoonDot} />
                        <span className={styles.comingSoonDot} />
                      </div>

                      {/* Pitch de conversão do Plus - vantagens em destaque (mesmo
                          layout de .benefitsList usado na tela de "pagamento
                          confirmado", pra reforçar visualmente o que o aluno está
                          desbloqueando). Copy sincronizada com a landing page. */}
                      <div className={styles.plusPitchHeader}>
                        <span className={styles.plusBadge}><FontAwesomeIcon icon={faStar} /> Plus</span>
                        <p className={styles.plusPitchLead}>
                          Desbloqueie o acompanhamento completo da sua evolução:
                        </p>
                      </div>
                      <div className={styles.benefitsList}>
                        {PLUS_BENEFITS.map(b => (
                          <div key={b.title} className={styles.benefitItem}>
                            <span className={styles.benefitIcon}><FontAwesomeIcon icon={b.icon} /></span>
                            <div>
                              <strong>{b.title}</strong>
                              <p>{b.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className={styles.plusPriceHint}>
                        A partir de <strong>R$ 29,00/mês</strong>
                      </p>

                      <p className={styles.comingSoonFreeIntro}>
                        Enquanto isso, o que já é seu de graça, para sempre:
                      </p>
                      <div className={styles.freeBenefitsGrid}>
                        {FREE_BENEFITS.map(b => (
                          <div key={b.title} className={styles.freeBenefitItem}>
                            <span className={styles.freeBenefitIcon}><FontAwesomeIcon icon={b.icon} /></span>
                            <strong>{b.title}</strong>
                            <p>{b.description}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <button type="button" className={styles.btnPrimary} onClick={onClose} style={{ width: "100%" }}>
                    Entendi
                  </button>
                </div>
              ) : (
                <>
                  {/* Reforço de valor na tela real de compra (planos já
                      cadastrados) - sem isso, o aluno só via preço e forma de
                      pagamento, sem nenhum motivo pra escolher o Plus em vez
                      de continuar no Free. Versão compacta (só ícone + título,
                      sem descrição) do mesmo PLUS_BENEFITS do "coming soon",
                      pra não empurrar os cards de plano pra baixo da dobra. */}
                  {!noPlusPitch && (SALES_PAYWALL ? (
                    /* ── VITRINE DE CONVERSÃO (flag SALES_PAYWALL) ── */
                    <div className={styles.vitrine}>
                      <div className={styles.vitrineHero}>
                        <h3 className={styles.vitrineHeroTitle}>
                          Seu treino é só o primeiro passo
                        </h3>
                        <p className={styles.vitrineHeroSub}>
                          A ficha você já tem. O Plus te <strong>acompanha toda semana</strong> enquanto seu corpo se adapta aos 90 dias de treino.
                        </p>
                        <button type="button" className={styles.vitrineSkipLink} onClick={scrollToPlans}>
                          Já sei o que quero, ver planos <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 10 }} />
                        </button>
                      </div>

                      {/* Free e Plus têm o MESMO intervalo de 90 dias entre renovações de
                          treino — é o tempo de adaptação do corpo, não uma trava de plano.
                          A diferença real: o Plus acompanha VOCÊ a cada 7 dias (fotos,
                          insights) durante esses 90 dias, e renova quantas vezes quiser
                          depois — o Free trava após a 1ª renovação. Nunca sugerir que o
                          Plus acelera o treino. Ver conversa sobre o modal de vendas. */}
                      <div className={styles.vitrineCompareGrid}>
                        <div className={styles.vitrineCompareCard}>
                          <div className={styles.vitrineCompareLabel}>Hoje (Free)</div>
                          <div className={styles.vitrineBars}>
                            {Array.from({ length: 7 }).map((_, i) => (
                              <span key={i} className={styles.vitrineBarFree} />
                            ))}
                          </div>
                          <div className={styles.vitrineCompareCaption}>90 dias sem acompanhamento · 1 renovação e trava</div>
                        </div>
                        <div className={`${styles.vitrineCompareCard} ${styles.vitrineCompareCardPlus}`}>
                          <div className={styles.vitrineCompareLabelPlus}>Com Plus</div>
                          <div className={styles.vitrineBars}>
                            {Array.from({ length: 7 }).map((_, i) => (
                              <span key={i} className={styles.vitrineBarPlus}>
                                {i % 2 === 0 && <span className={styles.vitrineBarDot} />}
                              </span>
                            ))}
                          </div>
                          <div className={styles.vitrineCompareCaptionPlus}>Acompanhado a cada 7 dias · renova sempre</div>
                        </div>
                      </div>

                      <div className={styles.vitrineSellingPoints}>
                        {PLUS_SELLING_POINTS.map(b => (
                          <div key={b.title} className={styles.vitrineSellingPoint}>
                            <FontAwesomeIcon icon={b.icon} className={styles.vitrineSellingIcon} />
                            <div>
                              <div className={styles.vitrineSellingTitle}>{b.title}</div>
                              <div className={styles.vitrineSellingDesc}>{b.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {activeStudents != null && activeStudents >= MIN_STUDENTS_TO_SHOW_PROOF && (
                        <div className={styles.vitrineSocialProof}>
                          <FontAwesomeIcon icon={faUsers} />
                          Junte-se a <strong>{activeStudents} alunos</strong> que já treinam no TreinoNexo
                        </div>
                      )}

                      <div className={styles.vitrineTrustRow}>
                        <FontAwesomeIcon icon={faCircleCheck} /> Cancele quando quiser · Pix seguro
                      </div>
                    </div>
                  ) : (
                    <div className={styles.plusFeatureStripWrap}>
                      <div
                        className={styles.plusFeatureStrip}
                        ref={plusStripElRef}
                        onScroll={checkPlusStripScroll}
                      >
                        {PLUS_BENEFITS.map(b => (
                          <span key={b.title} className={styles.plusFeatureChip}>
                            <FontAwesomeIcon icon={b.icon} /> {b.title}
                          </span>
                        ))}
                      </div>
                      {plusStripScrollable && (
                        <span className={styles.plusFeatureStripHint} aria-hidden="true">
                          <FontAwesomeIcon icon={faChevronRight} />
                        </span>
                      )}
                    </div>
                  ))}

                  {!isOrgOwner && (
                  <div className={styles.toggleRow}>
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${billingKind === "subscription" ? styles.toggleBtnActive : ""}`}
                      onClick={() => handleBillingKindChange("subscription")}
                    >
                      Assinar
                    </button>
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${billingKind === "oneoff" ? styles.toggleBtnActive : ""}`}
                      onClick={() => handleBillingKindChange("oneoff")}
                    >
                      Pagar avulso (1 mês)
                    </button>
                  </div>
                  )}

                  {billingKind === "oneoff" && monthlyPlans.length === 0 ? (
                    <div className={styles.emptyMsg}>Nenhum plano mensal disponível para pagamento avulso.</div>
                  ) : (
                  <div className={styles.planGrid} ref={planGridRef}>
                    {visiblePlans.map(plan => {
                      const months = MONTHS_PER_PLAN_INTERVAL[plan.interval] || 1;
                      const perDay = plan.amount ? plan.amount / (months * 30) : 0;
                      const effMonthly = plan.amount ? plan.amount / months : 0;
                      const savings = (SALES_PAYWALL && monthlyBaseline && effMonthly && effMonthly < monthlyBaseline)
                        ? Math.round((1 - effMonthly / monthlyBaseline) * 100) : 0;
                      const isBest = SALES_PAYWALL && plan.id === bestValuePlanId;
                      const isSelected = selectedPlanId === plan.id;
                      return (
                      <button
                        key={plan.id}
                        type="button"
                        className={`${styles.planCard} ${isSelected ? styles.planCardActive : ""} ${isBest && isSelected ? styles.planCardBest : ""}`}
                        onClick={() => setSelectedPlanId(plan.id)}
                      >
                        {isBest && (
                          <span className={styles.planBestBadge}>
                            Melhor valor
                          </span>
                        )}
                        <span className={styles.planName}>{plan.name}</span>
                        <span className={styles.planPrice}>{formatMoney(plan.amount)}</span>
                        {SALES_PAYWALL && plan.amount ? (
                          <>
                            <span className={styles.planPerDay}>
                              {formatMoney(perDay)}/dia
                            </span>
                            {savings > 0 && (
                              <span className={styles.planSavingsPill}>
                                economize {savings}%
                              </span>
                            )}
                          </>
                        ) : (plan.interval && plan.interval !== "monthly" && plan.amount ? (
                          <span style={{ fontStyle: "italic", fontSize: 11, color: "var(--text-0-50)", marginTop: 2 }}>
                            equivale a {formatMoney(plan.amount / months)}/mês
                          </span>
                        ) : null)}
                      </button>
                      );
                    })}
                  </div>
                  )}

                  <div className={styles.toggleRow}>
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${method === "pix" ? styles.toggleBtnActive : ""}`}
                      onClick={() => setMethod("pix")}
                    >
                      <FontAwesomeIcon icon={faQrcode} /> Pix
                    </button>
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${method === "credit_card" ? styles.toggleBtnActive : ""}`}
                      onClick={() => setMethod("credit_card")}
                    >
                      <FontAwesomeIcon icon={faCreditCard} /> Cartão
                    </button>
                  </div>

                  {needsCpf && (
                    <label className={styles.field}>
                      CPF/CNPJ
                      <input
                        value={cpfCnpj}
                        inputMode="numeric"
                        onChange={e => setCpfCnpj(e.target.value.replace(/\D/g, ""))}
                        placeholder="Somente números"
                      />
                    </label>
                  )}
                  {!needsCpf && currentUser?.cpf_cnpj && (
                    <label className={styles.field}>
                      CPF/CNPJ
                      <input
                        value={currentUser.cpf_cnpj}
                        disabled
                        title="Salvo em seu perfil"
                      />
                    </label>
                  )}

                  {method === "credit_card" && (
                    <div className={styles.cardFields}>
                      <label className={styles.field}>
                        Nome impresso no cartão
                        <input value={card.holder_name}
                          onChange={e => setCard(c => ({ ...c, holder_name: e.target.value }))} />
                      </label>
                      <label className={styles.field}>
                        Número do cartão
                        <input value={card.number} inputMode="numeric"
                          onChange={e => setCard(c => ({ ...c, number: e.target.value.replace(/\D/g, "") }))} />
                      </label>
                      <div className={styles.row3}>
                        <label className={styles.field}>
                          Mês (MM)
                          <input value={card.expiry_month} maxLength={2} inputMode="numeric"
                            onChange={e => setCard(c => ({ ...c, expiry_month: e.target.value.replace(/\D/g, "") }))} />
                        </label>
                        <label className={styles.field}>
                          Ano (AAAA)
                          <input value={card.expiry_year} maxLength={4} inputMode="numeric"
                            onChange={e => setCard(c => ({ ...c, expiry_year: e.target.value.replace(/\D/g, "") }))} />
                        </label>
                        <label className={styles.field}>
                          CVV
                          <input value={card.ccv} maxLength={4} inputMode="numeric"
                            onChange={e => setCard(c => ({ ...c, ccv: e.target.value.replace(/\D/g, "") }))} />
                        </label>
                      </div>
                      <div className={styles.row2}>
                        <label className={styles.field}>
                          CEP
                          <input value={holder.postal_code} inputMode="numeric"
                            onChange={e => setHolder(h => ({ ...h, postal_code: e.target.value.replace(/\D/g, "") }))} />
                        </label>
                        <label className={styles.field}>
                          Número do endereço
                          <input value={holder.address_number}
                            onChange={e => setHolder(h => ({ ...h, address_number: e.target.value }))} />
                        </label>
                      </div>
                      <label className={styles.field}>
                        Telefone/celular (com DDD)
                        <input value={holder.mobile_phone} inputMode="numeric" placeholder="Ex: 11999998888"
                          onChange={e => setHolder(h => ({ ...h, mobile_phone: e.target.value.replace(/\D/g, "") }))} />
                      </label>
                    </div>
                  )}

                  {submitting && (
                    <div className={styles.hint} role="status">
                      <FontAwesomeIcon icon={faSpinner} spin />{" "}
                      {method === "credit_card"
                        ? "Aguardando confirmação do cartão na Asaas… isso pode levar alguns segundos, não feche esta tela."
                        : "Gerando cobrança na Asaas…"}
                    </div>
                  )}
                  {submitError && <div className={styles.error}>{submitError}</div>}
                </>
              )}
            </div>
          )}
        </div>

        {!loading && !loadError && !result && !hasActiveSubscription && !hasCanceledButValidSubscription && !hasActiveOneOffAccess && plans.length > 0 && (
          <div className={styles.footer}>
            <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button type="button" className={styles.btnPrimary} onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Processando…</>
              ) : ctaLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
